import { Fragment, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import {
  alertIconsByType,
  type IAlertButtonAction,
  type IAlertSwitchAction,
  type IAlertProps,
  type IAlertLinkAction,
  type IAlertNavLinkAction,
} from 'shared/ui/Alert/types'
import { getAriaLabel, highlightUrls } from 'shared/lib'
import { IconButton } from 'shared/ui/IconButton'
import { Icon } from 'shared/ui/Icon'
import { Switch } from 'shared/ui/Switch'
import { NavLink } from 'shared/ui/NavLink'
import { uiStore } from 'shared/store/uiStore'

import styles from './styles.module.scss'

export const Alert: FC<IAlertProps> = observer(
  ({
    item,
    className,
    onClose,
    actions,
    customActions,
    fullWidthCustomActions,
    marginTop,
    marginBottom,
    rightActions,
    renderBottom,
    marginInline,
    ariaLabel = 'Alert',
  }) => {
    const { type, title, desc, hideIcon, hideBorder, icon, style } = item

    const onClickVueLink = (path: string) => {
      uiStore.changeRouteViaVue({ path })
    }

    const renderActions = (
      action: IAlertButtonAction | IAlertSwitchAction | IAlertLinkAction | IAlertNavLinkAction,
      idx: number
    ) => (
      <Fragment key={`${action.variant}_${idx}`}>
        {action.variant === 'button' && (
          <button
            className={classNames(styles.action, styles[action.type])}
            onClick={action.onClick}
            disabled={action.disabled}
            aria-label={getAriaLabel(`Button${action.text}`)}
          >
            {action.text}
          </button>
        )}

        {action.variant === 'link' && (
          <a
            href={action.link}
            target='_blank'
            rel='noopener noreferrer'
            className={classNames(styles.action, styles[action.type])}
            aria-label={getAriaLabel(`Button${action.text}`)}
          >
            {action.text}
          </a>
        )}

        {action.variant === 'switch' && (
          <Switch value={action.value} onChange={action.onClick} disabled={action.disabled} />
        )}

        {action.variant === 'navLink' && action.appType === 'react' && (
          <NavLink
            to={action.link}
            onClick={action.onClick}
            className={classNames(styles.action, styles[action.type])}
            aria-label={getAriaLabel(`Button${action.text}`)}
          >
            {action.text}
          </NavLink>
        )}

        {action.variant === 'navLink' && action.appType === 'vue' && (
          <button
            className={classNames(styles.action, styles[action.type])}
            onClick={() => {
              onClickVueLink(action.link)
              action.onClick?.()
            }}
            aria-label={getAriaLabel(`Button${action.text}`)}
          >
            {action.text}
          </button>
        )}
      </Fragment>
    )

    return (
      <div
        style={{ marginTop, marginBottom, marginInline }}
        className={className}
        aria-label={getAriaLabel('Alert')}
      >
        <div
          className={classNames(
            styles.item,
            styles[type],
            !title && styles.noTitle,
            hideBorder && styles.noBorder,
            fullWidthCustomActions && styles.itemColumn
          )}
          aria-label={getAriaLabel(ariaLabel, 'item')}
          style={style}
        >
          <div className={styles.itemRow}>
            <div className={styles.left} aria-label={getAriaLabel(ariaLabel, 'left')}>
              <div className={styles.leftContent}>
                {!hideIcon && (
                  <div className={styles.iconType} aria-label={getAriaLabel(ariaLabel, 'icon')}>
                    <Icon icon={icon || alertIconsByType[type]} />
                  </div>
                )}

                <div className={styles.content} aria-label={getAriaLabel(ariaLabel, 'content')}>
                  {title && (
                    <div className={styles.title} aria-label={getAriaLabel(ariaLabel, 'title')}>
                      {title}
                    </div>
                  )}
                  {desc && (
                    <div className={styles.desc} aria-label={getAriaLabel(ariaLabel, 'desc')}>
                      {highlightUrls(desc)}
                    </div>
                  )}
                  {actions && (
                    <div className={styles.actions} aria-label={getAriaLabel(ariaLabel, 'actions')}>
                      {actions.map(renderActions)}
                    </div>
                  )}
                  {!fullWidthCustomActions && customActions && (
                    <div
                      className={styles.customActions}
                      aria-label={getAriaLabel(ariaLabel, 'actions')}
                    >
                      {customActions}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {rightActions && (
              <div
                className={styles.rightActions}
                aria-label={getAriaLabel(ariaLabel, 'rightActions')}
              >
                {rightActions.map(renderActions)}
              </div>
            )}

            {onClose && (
              <div className={styles.iconClose} aria-label={getAriaLabel(ariaLabel, 'iconClose')}>
                <IconButton
                  icon={'close'}
                  size={'small'}
                  variant={'icon'}
                  color={'transparent'}
                  fontSize={16}
                  onClick={onClose}
                  ariaLabel='Alert_close'
                />
              </div>
            )}
          </div>
          {fullWidthCustomActions && customActions && (
            <div className={styles.customActions} aria-label={getAriaLabel(ariaLabel, 'actions')}>
              {customActions}
            </div>
          )}
        </div>

        {renderBottom && (
          <div className={styles.box} aria-label={getAriaLabel(ariaLabel, 'box')}>
            {renderBottom(item)}
          </div>
        )}
      </div>
    )
  }
)
